import React from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, CheckCircle2, Calendar, Clock, ArrowLeft, Building2, Minus, Plus } from 'lucide-react';
import { publicBookingApi } from '../../lib/api';

interface CatalogItem {
    id: string;
    label: string;
    unit_price_ttc: number;
    unit_type: string;
    default_duration_minutes: number;
}

const formatPrice = (amount: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(amount) || 0);
const formatDuration = (minutes: number) => {
    const total = Math.max(0, Math.round(Number(minutes) || 0));
    const h = Math.floor(total / 60);
    const m = total % 60;
    if (h === 0) return `${m} min`;
    if (m === 0) return `${h}h`;
    return `${h}h${String(m).padStart(2, '0')}`;
};
const todayInput = () => new Date().toISOString().split('T')[0];

const PoweredByFooter: React.FC = () => (
    <a
        href="https://gestion.l-iamani.com"
        target="_blank"
        rel="noopener noreferrer"
        className="mt-5 inline-flex items-center gap-1 text-[11px] text-content-muted hover:text-content transition-colors"
    >
        Propulsé par <span className="font-bold">L'IAmani</span>
    </a>
);

/** Widget public de réservation en ligne (aucune authentification) pour les clients Prestation — un
 *  visiteur choisit une ou plusieurs prestations (avec quantité), une date, un créneau, puis renseigne
 *  ses coordonnées. Le rendez-vous est créé au statut "demande" (non assigné), à valider ensuite côté
 *  établissement, exactement comme un rendez-vous créé manuellement depuis le Planning. */
export const BookingWidget: React.FC = () => {
    const { clientId } = useParams<{ clientId: string }>();
    const [phase, setPhase] = React.useState<'loading' | 'notfound' | 'select' | 'contact' | 'success' | 'error'>('loading');
    const [info, setInfo] = React.useState<{ company_name: string; logo_url: string; catalog: CatalogItem[]; location_mode?: 'domicile' | 'entreprise' | 'les_deux'; theme_color?: string } | null>(null);
    const [locationType, setLocationType] = React.useState<'domicile' | 'entreprise'>('domicile');

    // catalog_item_id -> quantité sélectionnée (0 = non sélectionné)
    const [quantities, setQuantities] = React.useState<Record<string, number>>({});
    const [selectedDate, setSelectedDate] = React.useState(todayInput());
    const [slots, setSlots] = React.useState<string[]>([]);
    const [loadingSlots, setLoadingSlots] = React.useState(false);
    const [selectedSlot, setSelectedSlot] = React.useState('');

    const [firstName, setFirstName] = React.useState('');
    const [lastName, setLastName] = React.useState('');
    const [phone, setPhone] = React.useState('');
    const [email, setEmail] = React.useState('');
    const [address, setAddress] = React.useState('');
    const [notes, setNotes] = React.useState('');
    const [submitting, setSubmitting] = React.useState(false);
    const [error, setError] = React.useState('');

    const selection = React.useMemo(() => {
        return (info?.catalog || [])
            .map((item) => ({ item, quantity: quantities[item.id] || 0 }))
            .filter((s) => s.quantity > 0);
    }, [info, quantities]);
    const hasSelection = selection.length > 0;
    const totalDuration = selection.reduce((sum, s) => sum + s.quantity * (Number(s.item.default_duration_minutes) || 0), 0);
    const totalPrice = selection.reduce((sum, s) => sum + s.quantity * (Number(s.item.unit_price_ttc) || 0), 0);

    const setQuantity = (itemId: string, qty: number) => {
        setQuantities((prev) => ({ ...prev, [itemId]: Math.max(0, Math.min(20, qty)) }));
    };

    // Page publique : toujours en mode clair, indépendamment du thème de l'établissement dans son propre
    // espace de gestion (qui reste, lui, en dark par défaut) — restaure le thème précédent au démontage.
    React.useEffect(() => {
        const root = document.documentElement;
        const previousTheme = root.getAttribute('data-theme');
        const wasDark = root.classList.contains('dark');
        root.setAttribute('data-theme', 'light');
        root.classList.remove('dark');
        root.classList.add('light');
        return () => {
            if (previousTheme) root.setAttribute('data-theme', previousTheme); else root.removeAttribute('data-theme');
            root.classList.toggle('dark', wasDark);
            root.classList.toggle('light', !wasDark);
        };
    }, []);

    React.useEffect(() => {
        if (!clientId) { setPhase('notfound'); return; }
        publicBookingApi.getInfo(clientId).then((data: any) => {
            if (!data || data.error) { setPhase('notfound'); return; }
            setInfo(data);
            setLocationType(data.location_mode === 'entreprise' ? 'entreprise' : 'domicile');
            setPhase('select');

            // Couleur de marque de l'établissement (si configurée), sinon couleur par défaut du site —
            // même mécanique que AuthContext.tsx pour un utilisateur connecté.
            const brandColor = data.theme_color || '#987b55';
            document.documentElement.style.setProperty('--brand-color', brandColor);
            const hex = brandColor.replace('#', '');
            const r = parseInt(hex.substring(0, 2), 16) || 0;
            const g = parseInt(hex.substring(2, 4), 16) || 0;
            const b = parseInt(hex.substring(4, 6), 16) || 0;
            const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
            document.documentElement.style.setProperty('--brand-fg', luminance > 0.5 ? '#000000' : '#ffffff');
        }).catch(() => setPhase('notfound'));
    }, [clientId]);

    const selectionKey = selection.map((s) => `${s.item.id}:${s.quantity}`).join(',');
    React.useEffect(() => {
        if (!clientId || !hasSelection || !selectedDate) { setSlots([]); return; }
        setLoadingSlots(true);
        setSelectedSlot('');
        publicBookingApi.getAvailability(clientId, selectedDate, selection.map((s) => ({ id: s.item.id, quantity: s.quantity }))).then((data: any) => {
            setSlots(Array.isArray(data?.slots) ? data.slots : []);
        }).catch(() => setSlots([])).finally(() => setLoadingSlots(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId, selectionKey, selectedDate]);

    const handleSubmit = async () => {
        if (!clientId || !hasSelection || !selectedSlot) return;
        if (!lastName.trim()) { setError('Merci de renseigner votre nom.'); return; }
        if (!email.trim()) { setError('Merci de renseigner votre email — indispensable pour recevoir la confirmation de votre rendez-vous.'); return; }
        setSubmitting(true);
        setError('');
        try {
            await publicBookingApi.reserve(clientId, {
                items: selection.map((s) => ({ catalog_item_id: s.item.id, quantity: s.quantity })),
                date: selectedDate, start_time: selectedSlot,
                first_name: firstName.trim(), last_name: lastName.trim(), phone: phone.trim(), email: email.trim(),
                address: address.trim(), notes: notes.trim(), location_type: locationType,
            });
            setPhase('success');
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de la réservation. Merci de réessayer.');
        } finally {
            setSubmitting(false);
        }
    };

    if (phase === 'loading') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-surface-app">
                <Loader2 size={32} className="animate-spin text-content-muted" />
            </div>
        );
    }

    if (phase === 'notfound') {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-surface-app p-4">
                <div className="text-center max-w-sm">
                    <Building2 size={32} className="mx-auto mb-3 text-content-muted opacity-40" />
                    <h1 className="text-base font-bold text-content mb-1.5">Réservation en ligne indisponible</h1>
                    <p className="text-content-muted text-xs">Ce lien de réservation n'est pas valide ou n'est plus actif.</p>
                </div>
                <PoweredByFooter />
            </div>
        );
    }

    if (phase === 'success') {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-surface-app p-4">
                <div className="text-center max-w-sm bg-surface border border-border rounded-2xl p-7 space-y-3">
                    <CheckCircle2 size={36} className="mx-auto text-emerald-500" />
                    <h1 className="text-base font-bold text-content">Réservation confirmée</h1>
                    <p className="text-content-muted text-xs leading-relaxed">
                        Votre rendez-vous pour <strong className="text-content">{selection.map((s) => s.quantity > 1 ? `${s.item.label} x${s.quantity}` : s.item.label).join(', ')}</strong> le{' '}
                        <strong className="text-content">{new Date(selectedDate + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</strong> à{' '}
                        <strong className="text-content">{selectedSlot}</strong> a bien été enregistré.
                    </p>
                    <p className="text-[11px] text-content-muted">Un email de confirmation vous a été envoyé{email ? ` à ${email}` : ''}.</p>
                </div>
                <PoweredByFooter />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-surface-app py-8 px-4">
            <div className="max-w-md mx-auto space-y-5">
                <header className="text-center space-y-1">
                    {info?.logo_url && <img src={info.logo_url} alt={info.company_name} className="h-10 mx-auto object-contain" />}
                    <h1 className="text-lg font-black text-content">{info?.company_name}</h1>
                    <p className="text-content-muted text-xs">Réservez votre créneau en ligne</p>
                </header>

                {phase === 'select' && (
                    <div className="bg-surface border border-border rounded-2xl p-4 space-y-4">
                        <div className="space-y-1.5">
                            <label className="text-[11px] font-bold text-content-muted uppercase tracking-widest">1. Choisissez une ou plusieurs prestations</label>
                            <div className="grid grid-cols-1 gap-1.5">
                                {(info?.catalog || []).map((item) => {
                                    const qty = quantities[item.id] || 0;
                                    return (
                                        <div
                                            key={item.id}
                                            className={`p-3 rounded-xl border transition-all flex items-center justify-between gap-2.5 ${
                                                qty > 0 ? 'border-brand-500 bg-brand-500/10' : 'border-border'
                                            }`}
                                        >
                                            <div className="min-w-0">
                                                <p className="font-bold text-content text-xs truncate">{item.label}</p>
                                                <p className="text-[11px] text-content-muted">Environ {formatDuration(item.default_duration_minutes)} · {formatPrice(item.unit_price_ttc)}</p>
                                            </div>
                                            {qty === 0 ? (
                                                <button
                                                    type="button"
                                                    onClick={() => setQuantity(item.id, 1)}
                                                    className="shrink-0 px-2.5 py-1 rounded-lg bg-brand-600 hover:bg-brand-700 text-brand-fg text-[11px] font-bold transition-all"
                                                >
                                                    Ajouter
                                                </button>
                                            ) : (
                                                <div className="shrink-0 flex items-center gap-1.5">
                                                    <button type="button" onClick={() => setQuantity(item.id, qty - 1)} className="w-6 h-6 rounded-md border border-brand-500/40 text-brand-600 flex items-center justify-center hover:bg-brand-500/10 transition-all">
                                                        <Minus size={12} />
                                                    </button>
                                                    <span className="w-4 text-center text-xs font-bold text-content">{qty}</span>
                                                    <button type="button" onClick={() => setQuantity(item.id, qty + 1)} className="w-6 h-6 rounded-md border border-brand-500/40 text-brand-600 flex items-center justify-center hover:bg-brand-500/10 transition-all">
                                                        <Plus size={12} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                {(!info?.catalog || info.catalog.length === 0) && (
                                    <p className="text-center text-content-muted text-xs py-5 italic">Aucune prestation disponible pour le moment.</p>
                                )}
                            </div>
                            {hasSelection && (
                                <div className="flex items-center justify-between text-[11px] font-bold text-content-muted pt-0.5 px-0.5">
                                    <span>Environ {formatDuration(totalDuration)} au total</span>
                                    <span className="text-content">{formatPrice(totalPrice)}</span>
                                </div>
                            )}
                        </div>

                        {hasSelection && (
                            <div className="space-y-1.5">
                                <label className="text-[11px] font-bold text-content-muted uppercase tracking-widest flex items-center gap-1.5">
                                    <Calendar size={11} /> 2. Choisissez une date
                                </label>
                                <input
                                    type="date"
                                    min={todayInput()}
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                    className="w-full bg-surface-soft border border-border rounded-lg px-3 py-2 text-xs text-content outline-none focus:border-brand-500 transition-colors"
                                />
                            </div>
                        )}

                        {hasSelection && selectedDate && (
                            <div className="space-y-1.5">
                                <label className="text-[11px] font-bold text-content-muted uppercase tracking-widest flex items-center gap-1.5">
                                    <Clock size={11} /> 3. Choisissez un horaire
                                </label>
                                {loadingSlots ? (
                                    <div className="flex justify-center py-5"><Loader2 size={18} className="animate-spin text-content-muted" /></div>
                                ) : slots.length === 0 ? (
                                    <p className="text-center text-content-muted text-xs py-5 italic">Aucun créneau disponible ce jour-là, essayez une autre date.</p>
                                ) : (
                                    <div className="grid grid-cols-4 gap-1.5">
                                        {slots.map((s) => (
                                            <button
                                                key={s}
                                                onClick={() => setSelectedSlot(s)}
                                                className={`py-2 rounded-lg border text-xs font-bold transition-all ${
                                                    selectedSlot === s ? 'border-brand-500 bg-brand-500/10 text-brand-600' : 'border-border text-content-muted hover:text-content'
                                                }`}
                                            >
                                                {s}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {selectedSlot && (
                            <button
                                onClick={() => setPhase('contact')}
                                className="w-full px-4 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold transition-all"
                            >
                                Continuer
                            </button>
                        )}
                    </div>
                )}

                {phase === 'contact' && hasSelection && (
                    <div className="bg-surface border border-border rounded-2xl p-4 space-y-3">
                        <button onClick={() => setPhase('select')} className="flex items-center gap-1 text-[11px] text-content-muted hover:text-content transition-colors">
                            <ArrowLeft size={13} /> Retour
                        </button>

                        <div className="bg-surface-soft rounded-xl p-3 text-xs space-y-1">
                            {selection.map((s) => (
                                <div key={s.item.id} className="flex items-center justify-between gap-3">
                                    <p className="font-bold text-content">{s.item.label}{s.quantity > 1 ? ` x${s.quantity}` : ''}</p>
                                    <p className="text-content-muted shrink-0">{formatPrice(s.quantity * s.item.unit_price_ttc)}</p>
                                </div>
                            ))}
                            <p className="text-content-muted pt-1 border-t border-border mt-1">
                                {new Date(selectedDate + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} à {selectedSlot}
                            </p>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-0.5">
                                <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Prénom</label>
                                <input value={firstName} onChange={(e) => setFirstName(e.target.value)} className="w-full bg-surface-soft border border-border rounded-lg px-2.5 py-2 text-xs text-content outline-none focus:border-brand-500" />
                            </div>
                            <div className="space-y-0.5">
                                <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Nom *</label>
                                <input value={lastName} onChange={(e) => setLastName(e.target.value)} className="w-full bg-surface-soft border border-border rounded-lg px-2.5 py-2 text-xs text-content outline-none focus:border-brand-500" />
                            </div>
                            <div className="space-y-0.5">
                                <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Téléphone</label>
                                <input value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full bg-surface-soft border border-border rounded-lg px-2.5 py-2 text-xs text-content outline-none focus:border-brand-500" />
                            </div>
                            <div className="space-y-0.5">
                                <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Email *</label>
                                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-surface-soft border border-border rounded-lg px-2.5 py-2 text-xs text-content outline-none focus:border-brand-500" />
                            </div>
                            {info?.location_mode === 'les_deux' && (
                                <div className="col-span-2 space-y-0.5">
                                    <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Lieu de la prestation</label>
                                    <div className="grid grid-cols-2 gap-1.5">
                                        <button type="button" onClick={() => setLocationType('domicile')}
                                            className={`px-2.5 py-2 rounded-lg text-[11px] font-bold border transition-all ${locationType === 'domicile' ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                            À mon domicile
                                        </button>
                                        <button type="button" onClick={() => setLocationType('entreprise')}
                                            className={`px-2.5 py-2 rounded-lg text-[11px] font-bold border transition-all ${locationType === 'entreprise' ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                            Dans leurs locaux
                                        </button>
                                    </div>
                                </div>
                            )}
                            {locationType === 'domicile' && (
                                <div className="col-span-2 space-y-0.5">
                                    <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Adresse d'intervention</label>
                                    <input value={address} onChange={(e) => setAddress(e.target.value)} className="w-full bg-surface-soft border border-border rounded-lg px-2.5 py-2 text-xs text-content outline-none focus:border-brand-500" />
                                </div>
                            )}
                            <div className="col-span-2 space-y-0.5">
                                <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Message (optionnel)</label>
                                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full bg-surface-soft border border-border rounded-lg px-2.5 py-2 text-xs text-content outline-none focus:border-brand-500 resize-none" />
                            </div>
                        </div>

                        {error && <p className="text-[11px] text-red-500 font-semibold">{error}</p>}

                        <button
                            onClick={handleSubmit}
                            disabled={submitting}
                            className="w-full px-4 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold disabled:opacity-50 transition-all"
                        >
                            {submitting ? 'Confirmation...' : 'Confirmer la réservation'}
                        </button>
                    </div>
                )}

                <div className="text-center">
                    <PoweredByFooter />
                </div>
            </div>
        </div>
    );
};
